import { Button, Modal as BootstrapModal } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import TextBox from '../controls/form/TextBox';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Product from '../../services/product';
import Toolbar from '../controls/Toolbar';
import PubSub from '../../services/PubSub';
import { useHistory } from 'react-router-dom';
import GenericDropdown from '../controls/GenericDropdown';
import GenericTextBox from '../controls/GenericTextBox';
import ExternalAuthValidator from '../../services/externalAuthValidator.js';

const ManageProducts = ({ products, show, onCancel }) => {
    const [translate] = useTranslation();
    const [authProviderConfigMapping, setAuthProviderConfigMapping] = useState({});
    const [selectedProduct, setSelectedProduct] = useState({});
    const [productAssigned, setProductAssigned] = useState(false);
    const [selectedAuthProvider, setSelectedAuthProvider] = useState('');
    const [authProviderConfig, setAuthProviderConfig] = useState({});

    const history = useHistory();

    const reset = useCallback(() => {
        setSelectedProduct({});
        setProductAssigned(false);
        setSelectedAuthProvider('');
        setAuthProviderConfig({});
    }, []);

    const getAuthProviderConfigMapping = useCallback(async () => {
        const data = await ExternalAuthValidator.getAuthProviderConfigMapping();
        if (data) {
            setAuthProviderConfigMapping(data);
        }
    }, []);

    useEffect(() => {
        if (show) {
            reset();
            getAuthProviderConfigMapping();
        }
    }, [show, reset, getAuthProviderConfigMapping]);

    const isProductAssigned = useCallback(async (id) => {
        const response = await Product.isProductAssigned(id);
        if (!response.error) {
            setProductAssigned(response.data.assigned);
        }
    }, []);

    useEffect(() => {
        if (selectedProduct._id) {
            isProductAssigned(selectedProduct._id);
        }
    }, [selectedProduct._id, isProductAssigned]);

    const update = useCallback(async ({ name, helpUrl, termsAndConditionsUrl }) => {
        const response = await Product.updateProduct(selectedProduct._id, { name, helpUrl, termsAndConditionsUrl, authProvider: selectedAuthProvider, authProviderConfig });
        if (!response.error) {
            reset();
            PubSub.publish('refresh-product-list');
        }
    }, [selectedProduct, selectedAuthProvider, authProviderConfig, reset]);

    const handleDeleteProduct = useCallback(async () => {
        const response = await Product.deleteProduct(selectedProduct._id);
        if (!response.error) {
            reset();
            history.push('/products');
            PubSub.publish('refresh-product-list');
        }
    }, [selectedProduct, reset, history]);

    const updateSelection = useCallback(async (productId) => {
        const product = products.filter(product => product._id === productId);
        setSelectedProduct(product[0]);
        if (product[0].authProvider) {
            setSelectedAuthProvider(product[0].authProvider);
        }
        else {
            setSelectedAuthProvider('');
        }
        setAuthProviderConfig(product[0].authProviderConfig || authProviderConfigMapping[product[0].authProvider]);
    }, [products, authProviderConfigMapping]);

    const renderToolbar = useMemo(() => (
        <Toolbar
            buttons={[
                {
                    name: 'Delete',
                    title: translate('OTM_DELETE'),
                    onClick: handleDeleteProduct,
                    disabled: !selectedProduct._id || productAssigned
                }
            ]}
            heading={translate('OTM_MANAGE_PRODUCTS')}
        />
    ), [translate, handleDeleteProduct, selectedProduct, productAssigned]);

    const renderProductTextBoxes = useMemo(() => {
        if (!selectedProduct._id) {
            return;
        }
        return (
            <>
                <TextBox
                    label={translate('OTM_PRODUCT_NAME')}
                    type='text'
                    name='name'
                    css='mt-3'
                />
                <TextBox
                    label={translate('OTM_HELP_URL')}
                    type='text'
                    name='helpUrl'
                    css='mt-3'
                />
                <TextBox
                    label={translate('OTM_TC_URL')}
                    type='text'
                    name='termsAndConditionsUrl'
                    css='mt-3'
                />
                <div className="mt-3">
                    <GenericDropdown
                        options={Object.keys(authProviderConfigMapping).map(key => ({ _id: key, name: key }))}
                        valueKey='_id'
                        displayValueKey='name'
                        addEmptyOption={true}
                        heading={translate('OTM_AUTH_PROVIDER')}
                        onSelect={(authProvider) => {
                            if (authProvider) {
                                setSelectedAuthProvider(authProvider);
                                setAuthProviderConfig(authProviderConfigMapping[authProvider]);
                            }
                            else {
                                setSelectedAuthProvider('');
                                setAuthProviderConfig({});
                            }
                        }}
                        defaultValue={selectedAuthProvider}
                        grow={true}
                    />

                </div>
            </>
        );
    }, [selectedProduct._id, selectedAuthProvider, authProviderConfigMapping, translate]);

    const renderAuthProviderTextBoxes = useMemo(() => {
        if (!selectedAuthProvider) {
            return;
        }

        return <div className="col-6">
            {authProviderConfigMapping[selectedAuthProvider] && Object.keys(authProviderConfigMapping[selectedAuthProvider]).map((key, index) => (
                <div className={`${index !== 0 ? "mt-3" : ""}`}>
                    <GenericTextBox
                        label={`${translate('OTM_SETTING_NAME_TO_RETRIEVE')} ${key}`}
                        type='text'
                        defaultValue={authProviderConfig[key]}
                        onChange={(event) => setAuthProviderConfig(prev => {
                            prev[key] = event.target.value;

                            return prev;
                        })}
                    />
                </div>
            ))}
        </div>
    }, [selectedAuthProvider, authProviderConfig, authProviderConfigMapping, translate]);

    return (
        <BootstrapModal show={show} backdrop='static' size="lg">
            <Formik
                initialValues={{
                    name: selectedProduct.name || '',
                    helpUrl: selectedProduct.helpUrl || '',
                    termsAndConditionsUrl: selectedProduct.termsAndConditionsUrl || '',
                }}
                validationSchema={Yup.object({
                    name: Yup.string()
                        .min(3, translate('OTM_CHARACTERS_OR_MORE', 3))
                        .required(' ')
                })}
                onSubmit={update}
                enableReinitialize
            >
                <Form>
                    <BootstrapModal.Header className='h5 text-secondary'>
                        {renderToolbar}
                    </BootstrapModal.Header>
                    <BootstrapModal.Body>
                        <div className="row">
                            <div className="col-6">
                                <GenericDropdown
                                    options={products}
                                    valueKey='_id'
                                    displayValueKey='name'
                                    heading={translate('OTM_SELECT_PRODUCT')}
                                    onSelect={updateSelection}
                                    defaultValue={selectedProduct.name}
                                    grow={true}
                                />
                                {renderProductTextBoxes}
                            </div>
                            {renderAuthProviderTextBoxes}
                        </div>
                    </BootstrapModal.Body>
                    <BootstrapModal.Footer>
                        <Button type='submit' className='btn-sm'>
                            {translate('OTM_UPDATE')}
                        </Button>
                        <Button
                            onClick={onCancel}
                            variant='secondary'
                            className='btn-sm'
                        >
                            {translate('OTM_CANCEL')}
                        </Button>
                    </BootstrapModal.Footer>
                </Form>
            </Formik>
        </BootstrapModal>
    );
};

export default ManageProducts;